import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Link,
  Paper,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { createTreatment, getPrescribers } from "../../api/treatment";
import { searchAnimalsQuick, getAnimalById } from "../../api/animal";
import {
  listMedicineProducts,
  listMedicineLots,
} from "../../api/medicine";
import { useSnackbar } from "notistack";
import { format } from "date-fns";
import { getTenantsList } from "../../api/tenant";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";

const animalLabel = (a) => {
  if (!a) return "";
  const tag = a.tag_number ?? "";
  const name = a.alias ?? "";
  if (tag && name) return `${tag} — ${name}`;
  return String(a.id ?? "");
};

const TreatmentForm = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [submissionError, setSubmissionError] = useState(null);

  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );

  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(null);

  const effectiveTenantId = isSuperAdmin
    ? tenantFilter?.id ?? null
    : user?.tenant_id ?? null;

  const farmTenantParam =
    isSuperAdmin && effectiveTenantId != null ? effectiveTenantId : null;

  const apiTenant = isSuperAdmin ? farmTenantParam : null;

  useEffect(() => {
    if (!isSuperAdmin) return;
    let c = false;
    (async () => {
      try {
        const raw = await getTenantsList({ for_filter: true });
        if (!c) setTenants(Array.isArray(raw) ? raw : []);
      } catch {
        if (!c) setTenants([]);
      }
    })();
    return () => {
      c = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin || tenants.length === 0 || tenantFilter != null) return;
    const raw =
      searchParams.get("tenant_id") ||
      searchParams.get("tenantId");
    if (!raw) return;
    const id = Number(raw);
    const t = tenants.find((x) => x.id === id);
    if (t) setTenantFilter(t);
  }, [isSuperAdmin, tenants, searchParams, tenantFilter]);

  const onOrgChange = (_, v) => {
    setTenantFilter(v);
    if (isSuperAdmin && v?.id != null) {
      const next = new URLSearchParams(searchParams);
      next.set("tenant_id", String(v.id));
      setSearchParams(next, { replace: true });
    }
  };

  const generateTreatmentId = () => {
    const datePart = format(new Date(), "yyyyMMdd");
    const randomPart = Math.floor(1000 + Math.random() * 9000);
    return `TRT-${datePart}-${randomPart}`;
  };

  const [animalOptions, setAnimalOptions] = useState([]);
  const [animalInput, setAnimalInput] = useState("");
  const [selectedAnimal, setSelectedAnimal] = useState(null);

  const [prescribers, setPrescribers] = useState([]);
  const [medProducts, setMedProducts] = useState([]);
  const [medProductInput, setMedProductInput] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [useStockLot, setUseStockLot] = useState(false);
  const [treatmentScope, setTreatmentScope] = useState("individual");
  const isGroupTreatment = treatmentScope === "group";

  const loadPrescribers = useCallback(async () => {
    if (isSuperAdmin && farmTenantParam == null) {
      setPrescribers([]);
      return;
    }
    if (!isSuperAdmin && user?.tenant_id == null) {
      setPrescribers([]);
      return;
    }
    try {
      const list = await getPrescribers(apiTenant);
      setPrescribers(list);
    } catch {
      setPrescribers([]);
    }
  }, [isSuperAdmin, farmTenantParam, user?.tenant_id, apiTenant]);

  useEffect(() => {
    loadPrescribers();
  }, [loadPrescribers]);

  useEffect(() => {
    let cancelled = false;
    if (isGroupTreatment) {
      setAnimalOptions([]);
      return;
    }
    const q = animalInput?.trim();
    if (!q || q.length < 1) {
      setAnimalOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      if (isSuperAdmin && farmTenantParam == null) return;
      if (!isSuperAdmin && user?.tenant_id == null) return;
      const rows = await searchAnimalsQuick(q, apiTenant);
      if (!cancelled) setAnimalOptions(rows);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    animalInput,
    apiTenant,
    isSuperAdmin,
    farmTenantParam,
    user?.tenant_id,
    isGroupTreatment,
  ]);

  useEffect(() => {
    let cancelled = false;
    const q = medProductInput.trim();
    if (!q || q.length < 1) {
      setMedProducts([]);
      return;
    }
    const t = setTimeout(async () => {
      if (isSuperAdmin && farmTenantParam == null) return;
      if (!isSuperAdmin && user?.tenant_id == null) return;
      try {
        const data = await listMedicineProducts(
          { page: 1, pageSize: 25, search: q, activeOnly: true },
          apiTenant
        );
        if (!cancelled) setMedProducts(data.items || []);
      } catch {
        if (!cancelled) setMedProducts([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    medProductInput,
    apiTenant,
    isSuperAdmin,
    farmTenantParam,
    user?.tenant_id,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedProduct?.id) {
        setLots([]);
        setSelectedLot(null);
        setUseStockLot(false);
        return;
      }
      if (isSuperAdmin && farmTenantParam == null) return;
      if (!isSuperAdmin && user?.tenant_id == null) return;
      try {
        const data = await listMedicineLots(
          { page: 1, pageSize: 100, productId: selectedProduct.id },
          apiTenant
        );
        const withStock = (data.items || []).filter(
          (lot) => Number(lot.quantity_remaining) > 0
        );
        if (!cancelled) {
          setLots(withStock);
          setSelectedLot(null);
        }
      } catch {
        if (!cancelled) setLots([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProduct, apiTenant, isSuperAdmin, farmTenantParam, user?.tenant_id]);

  const validationSchema = useMemo(() => {
    const prescriberPick = prescribers.length > 0;
    return Yup.object({
      treatment_scope: Yup.string()
        .oneOf(["individual", "group"])
        .required("Treatment type is required"),
      animal_id: Yup.number().when("treatment_scope", {
        is: "individual",
        then: (s) => s.required("Animal is required"),
        otherwise: (s) => s.nullable().notRequired(),
      }),
      group_name: Yup.string().when("treatment_scope", {
        is: "group",
        then: (s) => s.required("Group name is required for group treatment"),
        otherwise: (s) => s.notRequired(),
      }),
      treatment_of: Yup.string().required("Condition is required"),
      medicine: Yup.string().required("Medicine is required"),
      dosage: Yup.string().required("Dosage is required"),
      prescribed_by_user_id: prescriberPick
        ? Yup.number()
            .typeError("Select a prescriber")
            .required("Select a prescriber")
        : Yup.number().nullable(),
      prescribed_by: prescriberPick
        ? Yup.string()
        : Yup.string().required("Prescribed by is required"),
      dispensed_qty: Yup.string().when("medicine_lot_id", {
        is: (v) => v != null && v !== "",
        then: (s) =>
          s.required("Dispensed quantity is required when a lot is selected"),
        otherwise: (s) => s.notRequired(),
      }),
    });
  }, [prescribers.length]);

  const formik = useFormik({
    initialValues: {
      treatment_scope: treatmentScope,
      animal_id: "",
      group_name: "",
      group_size: "",
      application_method: "",
      treatment_id: generateTreatmentId(),
      signs_symptoms: "",
      treatment_of: "",
      medicine: "",
      brand: "",
      dosage: "",
      date: new Date(),
      time: "09:00:00",
      temperature: "",
      comment: "",
      next_dosage: null,
      prescribed_by: "",
      prescribed_by_user_id: null,
      medicine_product_id: null,
      medicine_lot_id: null,
      dispensed_qty: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmissionError(null);
      if (isSuperAdmin && farmTenantParam == null) {
        setSubmissionError("Select an organization first.");
        setSubmitting(false);
        return;
      }
      if (useStockLot && !selectedLot) {
        setSubmissionError("Select a stock lot or untick 'Dispensed from inventory stock lot'.");
        setSubmitting(false);
        return;
      }
      try {
        let timeVal = values.time;
        if (typeof timeVal === "string" && timeVal.length === 5) {
          timeVal = `${timeVal}:00`;
        }
        const formattedValues = {
          ...values,
          time: timeVal,
          date: format(values.date, "yyyy-MM-dd"),
          next_dosage: values.next_dosage
            ? format(values.next_dosage, "yyyy-MM-dd")
            : null,
          animal_id:
            values.treatment_scope === "individual"
              ? Number(values.animal_id)
              : null,
          group_size:
            values.treatment_scope === "group" && String(values.group_size).trim() !== ""
              ? Number(values.group_size)
              : null,
          medicine_product_id: values.medicine_product_id || null,
          medicine_lot_id: values.medicine_lot_id || null,
          prescribed_by_user_id: values.prescribed_by_user_id || null,
        };
        if (formattedValues.medicine_lot_id && formattedValues.dispensed_qty) {
          formattedValues.dispensed_qty = String(formattedValues.dispensed_qty).trim();
        } else {
          delete formattedValues.dispensed_qty;
          if (!formattedValues.medicine_lot_id) {
            delete formattedValues.medicine_lot_id;
          }
        }
        if (formattedValues.prescribed_by_user_id) {
          delete formattedValues.prescribed_by;
        }
        if (formattedValues.treatment_scope === "group") {
          delete formattedValues.animal_id;
        } else {
          delete formattedValues.group_name;
          delete formattedValues.group_size;
          delete formattedValues.application_method;
        }

        await createTreatment(formattedValues, apiTenant);
        enqueueSnackbar("Treatment created successfully!", {
          variant: "success",
        });
        const listUrl =
          farmTenantParam != null
            ? `/treatments?tenant_id=${farmTenantParam}`
            : "/treatments";
        navigate(listUrl);
      } catch (error) {
        const detail =
          error?.detail ||
          error?.message ||
          error?.response?.data?.detail ||
          "Failed to create treatment";
        if (String(detail).includes("Already Exists")) {
          const newId = generateTreatmentId();
          formik.setFieldValue("treatment_id", newId);
          setSubmissionError(
            "The treatment ID was already in use. A new one has been generated. Please submit again."
          );
        } else {
          setSubmissionError(String(detail));
        }
        enqueueSnackbar(String(detail), { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const animalQueryLoadedRef = useRef(null);
  useEffect(() => {
    if (isGroupTreatment) return;
    const preIdRaw = searchParams.get("animal_id");
    if (!preIdRaw) return;
    const aid = Number(preIdRaw);
    if (!Number.isFinite(aid)) return;
    if (isSuperAdmin && farmTenantParam == null) return;
    if (!isSuperAdmin && user?.tenant_id == null) return;
    const key = `${aid}:${apiTenant ?? "jwt"}`;
    if (animalQueryLoadedRef.current === key) return;
    let cancelled = false;
    (async () => {
      try {
        const a = await getAnimalById(aid, apiTenant);
        if (!cancelled && a) {
          animalQueryLoadedRef.current = key;
          setSelectedAnimal(a);
          formik.setFieldValue("animal_id", aid);
        }
      } catch {
        /* wrong tenant or missing */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.setFieldValue only
  }, [searchParams, apiTenant, isSuperAdmin, farmTenantParam, user?.tenant_id, isGroupTreatment]);

  if (isSuperAdmin && tenantFilter == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to="/treatments"
          >
            Treatments
          </Link>
          <Typography color="text.primary">New treatment</Typography>
        </Breadcrumbs>
        <Typography variant="h4" gutterBottom>
          Add treatment
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization before recording a treatment.
        </Alert>
        <Box sx={{ maxWidth: 480 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={onOrgChange}
            renderInput={(params) => (
              <TextField {...params} label="Organization" />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      </Box>
    );
  }

  if (!isSuperAdmin && user?.tenant_id == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          You need to belong to an organization to add treatments.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to={
            farmTenantParam != null
              ? `/treatments?tenant_id=${farmTenantParam}`
              : "/treatments"
          }
        >
          Treatments
        </Link>
        <Typography color="text.primary">New treatment</Typography>
      </Breadcrumbs>

      {isSuperAdmin && (
        <Box sx={{ maxWidth: 480, mb: 2 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={onOrgChange}
            renderInput={(params) => (
              <TextField {...params} label="Organization" />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Add treatment
        </Typography>

        {submissionError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submissionError}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Animal and timing
              </Typography>

              <TextField
                select
                fullWidth
                id="treatment_scope"
                name="treatment_scope"
                label="Treatment type"
                value={formik.values.treatment_scope}
                onChange={(e) => {
                  const scope = e.target.value;
                  setTreatmentScope(scope);
                  formik.setFieldValue("treatment_scope", scope);
                  if (scope === "group") {
                    formik.setFieldValue("animal_id", "");
                    setSelectedAnimal(null);
                  } else {
                    formik.setFieldValue("group_name", "");
                    formik.setFieldValue("group_size", "");
                    formik.setFieldValue("application_method", "");
                  }
                }}
                sx={{ mb: 2 }}
              >
                <MenuItem value="individual">Individual animal</MenuItem>
                <MenuItem value="group">Group / herd treatment</MenuItem>
              </TextField>

              {!isGroupTreatment ? (
                <Autocomplete
                  options={animalOptions}
                  value={selectedAnimal}
                  onChange={(_, v) => {
                    setSelectedAnimal(v);
                    formik.setFieldValue("animal_id", v?.id ?? "");
                  }}
                  onInputChange={(_, v) => setAnimalInput(v)}
                  getOptionLabel={(o) => animalLabel(o)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Animal *"
                      error={
                        formik.touched.animal_id &&
                        Boolean(formik.errors.animal_id)
                      }
                      helperText={
                        formik.touched.animal_id && formik.errors.animal_id
                      }
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              ) : (
                <>
                  <TextField
                    fullWidth
                    id="group_name"
                    name="group_name"
                    label="Group name *"
                    value={formik.values.group_name}
                    onChange={formik.handleChange}
                    error={formik.touched.group_name && Boolean(formik.errors.group_name)}
                    helperText={formik.touched.group_name && formik.errors.group_name}
                    sx={{ mb: 2 }}
                  />
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="group_size"
                        name="group_size"
                        label="Group size"
                        type="number"
                        value={formik.values.group_size}
                        onChange={formik.handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="application_method"
                        name="application_method"
                        label="Application method"
                        placeholder="e.g. spray, dip, waterline"
                        value={formik.values.application_method}
                        onChange={formik.handleChange}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <TextField
                fullWidth
                id="treatment_id"
                name="treatment_id"
                label="Treatment ID"
                value={formik.values.treatment_id}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />

              <DatePicker
                label="Date *"
                value={formik.values.date}
                onChange={(date) => formik.setFieldValue("date", date)}
                slotProps={{ textField: { fullWidth: true } }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                id="time"
                name="time"
                label="Time"
                type="time"
                value={
                  typeof formik.values.time === "string"
                    ? formik.values.time.slice(0, 5)
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value;
                  formik.setFieldValue(
                    "time",
                    v?.length === 5 ? `${v}:00` : v
                  );
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Condition
              </Typography>
              <TextField
                fullWidth
                id="treatment_of"
                name="treatment_of"
                label="Condition *"
                value={formik.values.treatment_of}
                onChange={formik.handleChange}
                error={
                  formik.touched.treatment_of &&
                  Boolean(formik.errors.treatment_of)
                }
                helperText={
                  formik.touched.treatment_of && formik.errors.treatment_of
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                id="signs_symptoms"
                name="signs_symptoms"
                label="Signs/Symptoms"
                value={formik.values.signs_symptoms}
                onChange={formik.handleChange}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                id="temperature"
                name="temperature"
                label="Temperature"
                value={formik.values.temperature}
                onChange={formik.handleChange}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Medicine
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(12, minmax(0, 1fr))",
                  },
                  "& .MuiFormControl-root, & .MuiFormControlLabel-root": {
                    width: "100%",
                  },
                }}
              >
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / span 6" } }}>
                  <Autocomplete
                    options={medProducts}
                    value={selectedProduct}
                    onChange={(_, v) => {
                      setSelectedProduct(v);
                      formik.setFieldValue(
                        "medicine_product_id",
                        v?.id ?? null
                      );
                      formik.setFieldValue("medicine", v?.name ?? "");
                      formik.setFieldValue("brand", v?.brand ?? "");
                      formik.setFieldValue("medicine_lot_id", null);
                      formik.setFieldValue("dispensed_qty", "");
                      setSelectedLot(null);
                      setUseStockLot(false);
                    }}
                    onInputChange={(_, v) => setMedProductInput(v)}
                    getOptionLabel={(o) =>
                      o
                        ? `${o.name}${o.brand ? ` (${o.brand})` : ""} · ID ${o.id}`
                        : ""
                    }
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Formulary product (optional)"
                        helperText="Pick the exact medicine product. Stock lots are matched by this Product ID."
                      />
                    )}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "7 / span 6" } }}>
                  <FormControlLabel
                    sx={{ mb: 1, minHeight: 40, alignItems: "center" }}
                    control={
                      <Checkbox
                        checked={useStockLot}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseStockLot(checked);
                          if (!checked) {
                            setSelectedLot(null);
                            formik.setFieldValue("medicine_lot_id", null);
                            formik.setFieldValue("dispensed_qty", "");
                          }
                        }}
                        disabled={!selectedProduct}
                      />
                    }
                    label="Dispensed from inventory stock lot"
                  />
                  <Autocomplete
                    options={lots}
                    value={selectedLot}
                    onChange={(_, v) => {
                      setSelectedLot(v);
                      formik.setFieldValue("medicine_lot_id", v?.id ?? null);
                      if (v?.unit) {
                        /* unit set server-side on deduct */
                      }
                    }}
                    getOptionLabel={(lot) =>
                      lot
                        ? `${selectedProduct?.name || "Product"} · lot #${lot.id} · ${lot.batch_number || "batch"} · ${lot.quantity_remaining} ${lot.unit}`
                        : ""
                    }
                    disabled={!selectedProduct || !useStockLot}
                    noOptionsText={
                      selectedProduct
                        ? `No stock lots found for ${selectedProduct.name} (Product ID ${selectedProduct.id}). Receive stock for this same product in Medicine cabinet.`
                        : "Select a formulary product first."
                    }
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Stock lot (deduct inventory)"
                        helperText={
                          selectedProduct
                            ? `Pick the lot used for this dose (${selectedProduct.name}, ID ${selectedProduct.id}). Saving will deduct quantity from this lot.`
                            : "Select a formulary product first to see available lots."
                        }
                      />
                    )}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / span 3" } }}>
                  <TextField
                    fullWidth
                    id="medicine"
                    name="medicine"
                    label="Medicine *"
                    value={formik.values.medicine}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.medicine &&
                      Boolean(formik.errors.medicine)
                    }
                    helperText={
                      formik.touched.medicine && formik.errors.medicine
                    }
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "4 / span 3" } }}>
                  <TextField
                    fullWidth
                    id="brand"
                    name="brand"
                    label="Brand"
                    value={formik.values.brand}
                    onChange={formik.handleChange}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: {
                      xs: "1 / -1",
                      md: useStockLot ? "7 / span 3" : "7 / span 6",
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    id="dosage"
                    name="dosage"
                    label="Dosage *"
                    value={formik.values.dosage}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.dosage && Boolean(formik.errors.dosage)
                    }
                    helperText={
                      formik.touched.dosage && formik.errors.dosage
                    }
                  />
                </Box>
                {useStockLot && (
                  <Box sx={{ gridColumn: { xs: "1 / -1", md: "10 / span 3" } }}>
                    <TextField
                      fullWidth
                      id="dispensed_qty"
                      name="dispensed_qty"
                      label={`Dispensed quantity${selectedLot?.unit ? ` (${selectedLot.unit})` : ""} *`}
                      value={formik.values.dispensed_qty}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.dispensed_qty &&
                        Boolean(formik.errors.dispensed_qty)
                      }
                      helperText={
                        formik.touched.dispensed_qty &&
                        formik.errors.dispensed_qty
                      }
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Prescriber
              </Typography>
              {prescribers.length > 0 ? (
                <Autocomplete
                  options={prescribers}
                  getOptionLabel={(p) => p.label || p.email || `#${p.id}`}
                  value={
                    prescribers.find(
                      (p) => p.id === formik.values.prescribed_by_user_id
                    ) || null
                  }
                  onChange={(_, v) => {
                    formik.setFieldValue(
                      "prescribed_by_user_id",
                      v?.id ?? null
                    );
                    formik.setFieldValue(
                      "prescribed_by",
                      v?.label || v?.email || ""
                    );
                  }}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Prescribing staff *"
                      error={
                        formik.touched.prescribed_by &&
                        Boolean(formik.errors.prescribed_by)
                      }
                      helperText={
                        (formik.touched.prescribed_by ||
                          formik.touched.prescribed_by_user_id) &&
                        (formik.errors.prescribed_by ||
                          formik.errors.prescribed_by_user_id)
                      }
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              ) : (
                <TextField
                  fullWidth
                  id="prescribed_by"
                  name="prescribed_by"
                  label="Prescribed by *"
                  value={formik.values.prescribed_by}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.prescribed_by &&
                    Boolean(formik.errors.prescribed_by)
                  }
                  helperText={
                    formik.touched.prescribed_by && formik.errors.prescribed_by
                  }
                  sx={{ mb: 2 }}
                />
              )}
              <DatePicker
                label="Next dosage date"
                value={formik.values.next_dosage}
                onChange={(date) => formik.setFieldValue("next_dosage", date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} sx={{ width: "100%" }}>
              <Typography variant="h6" gutterBottom>
                Comments
              </Typography>
              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  id="comment"
                  name="comment"
                  label="Comments"
                  value={formik.values.comment}
                  onChange={formik.handleChange}
                  multiline
                  minRows={4}
                  sx={{ width: "100%", display: "block" }}
                />
              </Box>
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(
                      farmTenantParam != null
                        ? `/treatments?tenant_id=${farmTenantParam}`
                        : "/treatments"
                    )
                  }
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {formik.isSubmitting ? "Saving..." : "Save treatment"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default TreatmentForm;
